<!--
 * @Description: 
 * @Author: luckymiaow
 * @Date: 2022-07-11 16:38:40
 * @LastEditors: luckymiaow
-->

<script setup lang="ts">
import type { DataNode } from 'ant-design-vue/lib/tree';
import type { FieldNames } from 'ant-design-vue/lib/vc-cascader';
import type { TreeProps } from 'ant-design-vue/lib/vc-tree/props';
import type { PropType } from 'vue';
import { computed, ref, useAttrs, watch } from 'vue';
import useConfigProvider from '../../config-provider/src/useConfigProvider';
import type { ITreeTool, OperatingType, TreeNode, TreeNodeSlotsNode } from '../types';
import BaseTree from './BaseTree.vue';

const props = defineProps({
  treeData: {
    type: Array as PropType<DataNode[] | Object[]>,
  },
  fieldNames: {
    type: Object as PropType<FieldNames>,
    default: () => ({ children: 'children', title: 'title', key: 'key' }),
  },
});

const injectConfig = useConfigProvider().injectConfig<TreeProps>('Tree');

const attrs = useAttrs();

const _treeData = ref<TreeNode<unknown>[]>([]);

function fieldConvert(data: any[], path?: string): TreeNode<unknown>[] {
  return data.map((e: { [x: string]: any }) => {
    const value = path ? `${path}/${e[props.fieldNames.title]}` : e[props.fieldNames.title];
    return {
      title: e[props.fieldNames.title],
      children: fieldConvert(e[props.fieldNames.children], value),
      data: e,
      value,
      key: e[props.fieldNames.key],
    };
  });
}

watch(
  () => props.treeData,
  () => {
    if (props.fieldNames != null) {
      _treeData.value = fieldConvert(props.treeData);
    }
  },
  { immediate: true }
);

const getBindValue = computed<any>(() => ({
  ...injectConfig?.value,
  ...attrs,
  treeData: _treeData.value,
}));

const treeRef = ref<ITreeTool<unknown>>();

function addTreeNode() {
  return treeRef.value?.addTreeNode();
}

function fetchTreeNode<T>(nodes: TreeNode<T>[]) {
  return nodes.map(node => {
    return {
      ...node.data,
      children: fetchTreeNode(node.children),
    };
  });
}

function change(
  data: TreeNodeSlotsNode<unknown>,
  { type, treeData }: { type: OperatingType; treeData: TreeNode<unknown>[] }
) {
  data.data = {
    [props.fieldNames.title]: data.title,
    [props.fieldNames.children]: data.children,
    [props.fieldNames.key]: data.title,
  };
}

const fetchTree = computed(() => fetchTreeNode(treeRef.value.treeData));

defineExpose({ addTreeNode, treeData: fetchTree });
</script>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<template>
  <base-tree v-bind="getBindValue" ref="treeRef" @change="change"
    ><template v-for="(slot, key) in $slots" :key="key" #[key]="scope">
      <slot :name="key" v-bind="scope ?? {}"> </slot>
    </template>
  </base-tree>
</template>
